/**
 * F2-c: sezione Listini hotel (owner).
 *
 * Per ogni tipologia (room_type) l'owner edita i prezzi del listino collegato
 * (pricing_rule): tariffa 2h, supplemento 3a ora, ora 4+, pernottamento +
 * maggiorazioni weekend/festivi. Salvataggio per-tipologia via PATCH
 * /owner/room-types/:id (aggiorna la pricing_rule collegata, D3) + invalidate.
 *
 * D3: pricing_rules e' centralizzata; qui editiamo i prezzi del listino
 * collegato alla singola tipologia di QUESTO hotel.
 */
import { Check, TriangleAlert, Wallet } from '@hotel-manager/ui/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type HotelPricingRule,
  type HotelRoomType,
  patchHotelRoomType,
} from '../../../api/owner.js';

interface Props {
  tenantId: string;
  roomTypes: HotelRoomType[];
  pricingRules: HotelPricingRule[];
}

export function ListiniSection({ tenantId, roomTypes, pricingRules }: Props) {
  const ruleById = new Map(pricingRules.map((r) => [r.id, r]));
  const sorted = [...roomTypes].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <section data-testid="listini-section" className="flex flex-col gap-4">
      <header>
        <h2 className="text-base font-bold text-text-primary flex items-center gap-1.5">
          <Wallet size={16} strokeWidth={1.75} className="text-accent-primary" />
          Listini prezzi
        </h2>
        <p className="text-xs text-text-secondary mt-0.5">
          Tariffe per tipologia camera. Day use a scaglioni (2h / 3a ora / ore 4+) e pernottamento.
        </p>
      </header>

      {sorted.length === 0 && (
        <div className="rounded-2xl border border-border-base bg-bg-card p-8 text-center text-text-muted shadow-hm-sm">
          Nessuna tipologia configurata per questo hotel.
        </div>
      )}

      <div className="flex flex-col gap-4">
        {sorted.map((rt) => (
          <RoomTypePricingCard
            key={rt.id}
            tenantId={tenantId}
            roomType={rt}
            rule={rt.pricingRuleId ? (ruleById.get(rt.pricingRuleId) ?? null) : null}
          />
        ))}
      </div>
    </section>
  );
}

type PriceForm = {
  base2hPrice: string;
  hour3Supplement: string;
  hour4plusUnit: string;
  overnightPrice: string;
  weekendSurchargePct: string;
  holidaySurchargePct: string;
};

function RoomTypePricingCard({
  tenantId,
  roomType,
  rule,
}: {
  tenantId: string;
  roomType: HotelRoomType;
  rule: HotelPricingRule | null;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<PriceForm>(() => ({
    base2hPrice: rule?.base2hPrice ?? '',
    hour3Supplement: rule?.hour3Supplement ?? '',
    hour4plusUnit: rule?.hour4plusUnit ?? '',
    overnightPrice: rule?.overnightPrice ?? '',
    weekendSurchargePct: String(rule?.weekendSurchargePct ?? 0),
    holidaySurchargePct: String(rule?.holidaySurchargePct ?? 0),
  }));
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      patchHotelRoomType(roomType.id, {
        pricing: {
          base2hPrice: num(form.base2hPrice),
          hour3Supplement: num(form.hour3Supplement),
          hour4plusUnit: num(form.hour4plusUnit),
          overnightPrice: num(form.overnightPrice),
          weekendSurchargePct: int(form.weekendSurchargePct),
          holidaySurchargePct: int(form.holidaySurchargePct),
        },
      }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['owner', 'hotel', tenantId] });
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  function set<K extends keyof PriceForm>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  if (!rule) {
    return (
      <div
        data-testid={`pricing-card-${roomType.code}`}
        className="rounded-2xl border border-border-base bg-bg-card p-5 shadow-hm-sm"
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-text-primary">{roomType.label}</h3>
          <span className="text-[11px] font-mono text-text-muted">{roomType.code}</span>
        </div>
        <p className="text-sm text-text-muted mt-2">
          Nessun listino collegato a questa tipologia. Collega un listino dalle impostazioni hotel
          per modificarne i prezzi qui.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid={`pricing-card-${roomType.code}`}
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="rounded-2xl border border-border-base bg-bg-card p-5 sm:p-6 shadow-hm-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold text-text-primary">{roomType.label}</h3>
          <p className="text-[11px] text-text-muted mt-0.5">Listino: {rule.name}</p>
        </div>
        <span className="text-[11px] font-mono text-text-muted">{roomType.code}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <PriceField
          label="Tariffa 2h"
          testid={`price-base2h-${roomType.code}`}
          value={form.base2hPrice}
          onChange={(v) => set('base2hPrice', v)}
          suffix="€"
        />
        <PriceField
          label="Supplemento 3a ora"
          value={form.hour3Supplement}
          onChange={(v) => set('hour3Supplement', v)}
          suffix="€"
        />
        <PriceField
          label="Ora 4+ (unitaria)"
          value={form.hour4plusUnit}
          onChange={(v) => set('hour4plusUnit', v)}
          suffix="€"
        />
        <PriceField
          label="Pernottamento"
          testid={`price-overnight-${roomType.code}`}
          value={form.overnightPrice}
          onChange={(v) => set('overnightPrice', v)}
          suffix="€"
        />
        <PriceField
          label="Maggior. weekend"
          value={form.weekendSurchargePct}
          onChange={(v) => set('weekendSurchargePct', v)}
          suffix="%"
        />
        <PriceField
          label="Maggior. festivi"
          value={form.holidaySurchargePct}
          onChange={(v) => set('holidaySurchargePct', v)}
          suffix="%"
        />
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <button
          type="submit"
          disabled={mutation.isPending}
          data-testid={`pricing-save-${roomType.code}`}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors disabled:opacity-50"
        >
          {mutation.isPending ? 'Salvataggio…' : 'Salva prezzi'}
        </button>
        {saved && (
          <output className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5F8A50]">
            <Check size={16} strokeWidth={2} /> Salvato
          </output>
        )}
        {mutation.isError && (
          <span
            role="alert"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#A8453A]"
          >
            <TriangleAlert size={16} strokeWidth={1.75} /> Errore. Riprova.
          </span>
        )}
      </div>
    </form>
  );
}

function PriceField({
  label,
  value,
  onChange,
  suffix,
  testid,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suffix: string;
  testid?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium text-text-secondary">{label}</span>
      <div className="relative">
        <input
          data-testid={testid}
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-border-input bg-bg-card pl-3 pr-8 h-10 text-sm text-text-primary outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary transition-colors"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">
          {suffix}
        </span>
      </div>
    </label>
  );
}

function num(s: string): number {
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}
function int(s: string): number {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : 0;
}
